import React from "react";
import { ArrowUpRight, Users, Flame } from "lucide-react";
import { useContent } from "../lib/content";

export default function Community() {
  const kicker = useContent("page.community.kicker", "/ 08 — COMMUNITY");
  const FB_URL = useContent("community.fb_url", "https://www.facebook.com/groups/219054352932146");
  const YATRA_URL = useContent("community.yatra_url", "https://www.yatrafestival.co.nz");
  const SOUNDATION_URL = useContent("community.soundation_url", "https://soundation.live");
  const fbLogo = useContent("community.fb.logo", "");
  const fbImage = useContent("community.fb.image", "");
  const soundationLogo = useContent(
    "community.soundation.logo",
    "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/28vb7n50_Logo%20Sound%20met3d.png"
  );
  const soundationImage = useContent(
    "community.soundation.image",
    "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/tsmh4tit_Soundation%20Letra.png"
  );
  const yatraLogo = useContent("community.yatra.logo", "");
  const yatraImage = useContent("community.yatra.image", "");
  const pageTitle = useContent("page.community.title", "THE SCENE BEHIND THE SOUND");
  const pageIntro = useContent(
    "page.community.intro",
    "Inner Pulse is deeply rooted in the South Island techno community. Explore the projects, collectives and spaces that hold this world together."
  );
  const fbTitle = useContent("page.community.fb_title", "Techno & House in the South Island");
  const fbBody = useContent(
    "page.community.fb_body",
    "A dedicated space created to connect techno & house lovers across Te Waipounamu. Event announcements, local lineups and the pulse of the southern scene."
  );
  const soundationBody = useContent(
    "page.community.soundation_body",
    "TechMeOver · Suave · SŌMA · Summer Technival · Easter Reconexion. The artistic umbrella for every event Sandrita curates across the South Island."
  );
  const yatraTitle = useContent("page.community.yatra_title", "Yatra Festival");
  const yatraBody = useContent(
    "page.community.yatra_body",
    "Takaka — Aotearoa. Showcasing techno artists from across the country plus internationals. Sandrita is the main coordinator of the Pulse stage."
  );

  return (
    <div data-testid="community-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="community-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {pageTitle}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-2xl mt-8 leading-relaxed">
            {pageIntro}
          </p>
        </div>

        <div className="flex flex-col" data-testid="community-list">
          {/* 01 — Facebook group */}
          <a
            href={FB_URL}
            target="_blank"
            rel="noreferrer"
            className="group hairline-t py-10 grid md:grid-cols-12 gap-6 items-start hover:pl-6 transition-all duration-500"
            data-testid="community-link-01"
          >
            <div className="md:col-span-1 font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
              / 01
            </div>
            <div className="md:col-span-1">
              <div className="w-20 h-20 hairline flex items-center justify-center overflow-hidden text-silver-300 group-hover:text-silver-100 group-hover:border-silver-200 transition-all">
                {fbLogo ? (
                  <img
                    src={fbLogo}
                    alt="Facebook"
                    className="w-full h-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <Users size={32} />
                )}
              </div>
            </div>
            <div className="md:col-span-7 flex flex-col gap-2">
              <div className="font-title text-[0.58rem] tracking-[0.3em] text-silver-600">
                FACEBOOK GROUP
              </div>
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.08em] text-silver-100 group-hover:text-silver-glow transition-all">
                {fbTitle}
              </h2>
              <div className="font-title text-[0.65rem] tracking-[0.22em] text-silver-500 uppercase">
                Aotearoa — Facebook Community
              </div>
              <p className="font-body text-sm text-silver-400 leading-relaxed mt-2 max-w-xl">
                {fbBody}
              </p>
              {fbImage && (
                <div className="mt-4 overflow-hidden">
                  <img
                    src={fbImage}
                    alt="Facebook banner"
                    className="w-full h-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-700"
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-3 flex md:justify-end items-start">
              <span className="flex items-center gap-2 font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 group-hover:text-silver-100 transition-colors">
                Visit
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </span>
            </div>
          </a>

          {/* 02 — Soundation — featured with big metallic title */}
          <a
            href={SOUNDATION_URL}
            target="_blank"
            rel="noreferrer"
            className="group hairline-t py-14 flex flex-col gap-6 hover:pl-6 transition-all duration-500"
            data-testid="community-link-02"
          >
            <div className="grid md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-1 font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                / 02
              </div>
              <div className="md:col-span-1">
                <div className="w-20 h-20 hairline flex items-center justify-center overflow-hidden p-1">
                  <img
                    src={soundationLogo}
                    alt="Soundation"
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform"
                    style={{ filter: "brightness(1.15)" }}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              </div>
              <div className="md:col-span-7 flex flex-col gap-2">
                <div className="font-title text-[0.58rem] tracking-[0.3em] text-silver-600">
                  SOUNDATION.LIVE
                </div>
                <div className="font-title text-[0.65rem] tracking-[0.22em] text-silver-500 uppercase">
                  Event Series & Community
                </div>
                <p className="font-body text-sm text-silver-400 leading-relaxed mt-2 max-w-xl">
                  {soundationBody}
                </p>
              </div>
              <div className="md:col-span-3 flex md:justify-end items-start">
                <span className="flex items-center gap-2 font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 group-hover:text-silver-100 transition-colors">
                  Visit
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </span>
              </div>
            </div>

            {/* Big metallic SOUNDATION title image */}
            {soundationImage && (
              <div className="mt-6 overflow-hidden flex justify-start" data-testid="community-soundation-title">
                <img
                  src={soundationImage}
                  alt="SOUNDATION"
                  className="w-full h-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-700"
                  style={{ maxHeight: "120px", maxWidth: "560px", filter: "brightness(1.15) drop-shadow(0 0 18px rgba(198,198,198,0.08))" }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            )}
          </a>

          {/* 03 — Yatra Festival */}
          <a
            href={YATRA_URL}
            target="_blank"
            rel="noreferrer"
            className="group hairline-t py-10 grid md:grid-cols-12 gap-6 items-start hover:pl-6 transition-all duration-500"
            data-testid="community-link-03"
          >
            <div className="md:col-span-1 font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
              / 03
            </div>
            <div className="md:col-span-1">
              <div className="w-20 h-20 hairline flex items-center justify-center overflow-hidden text-silver-300 group-hover:text-silver-100 group-hover:border-silver-200 transition-all">
                {yatraLogo ? (
                  <img
                    src={yatraLogo}
                    alt="Yatra"
                    className="w-full h-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <Flame size={32} />
                )}
              </div>
            </div>
            <div className="md:col-span-7 flex flex-col gap-2">
              <div className="font-title text-[0.58rem] tracking-[0.3em] text-silver-600">
                YATRA
              </div>
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.08em] text-silver-100 group-hover:text-silver-glow transition-all">
                {yatraTitle}
              </h2>
              <div className="font-title text-[0.65rem] tracking-[0.22em] text-silver-500 uppercase">
                Pulse Stage Main Coordinator
              </div>
              <p className="font-body text-sm text-silver-400 leading-relaxed mt-2 max-w-xl">
                {yatraBody}
              </p>
              {yatraImage && (
                <div className="mt-4 overflow-hidden">
                  <img
                    src={yatraImage}
                    alt="Yatra banner"
                    className="w-full h-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-700"
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-3 flex md:justify-end items-start">
              <span className="flex items-center gap-2 font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 group-hover:text-silver-100 transition-colors">
                Visit
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </span>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
